/**
 * **Les doublons probables, calculés dans le navigateur** *(K4, `doublons.md` §4)*.
 *
 * Le serveur porte deux empreintes par entité et ne compare rien : `VisualHash`, un
 * pHash de 64 bits de la vignette, et `Sha256`, l'empreinte exacte de la source. Ce
 * module les compare **toutes deux à deux**, au seuil que l'utilisateur a réglé
 * (2 par défaut, jusqu'à 20 — DB10), et rend des **groupes**.
 *
 * - **Deux photos sont voisines** si leurs `VisualHash` sont à une distance de
 *   Hamming ≤ seuil, **ou** si leurs `Sha256` sont égaux. Le second cas couvre une
 *   vidéo importée de force, qui n'a pas d'empreinte visuelle.
 * - **Un groupe se forme de proche en proche** : A voisine de B et B de C font un
 *   groupe même si A et C sont loin. À 2, ce sont des doublons vrais ; vers 20, les
 *   groupes s'enchaînent, et c'est le prix d'un réglage large.
 *
 * **Le coût** : un XOR et deux comptes de bits par paire. Mesuré en JavaScript :
 * 6 ms à 1 000 photos, 160 ms à 10 000, 1,3 s à 30 000. Le calcul se refait quand
 * le miroir ou le seuil change ; on ne garde pas les paires jusqu'à 20, qui
 * feraient ~3 millions d'entrées à 30 000 photos.
 *
 * Le module est **pur** : aucune dépendance à l'état, mesurable sans compte.
 */

export const DEFAULT_THRESHOLD = 2;
export const MAX_THRESHOLD = 20;

/** Ce que le module lit d'une entité — rien de plus. */
export interface Fingerprinted {
  readonly Fid?: string;
  readonly VisualHash?: string;
  readonly Sha256?: string;
}

const HEX16 = /^[0-9a-f]{16}$/;

/** Le compte de bits d'un entier de 32 bits, sans boucle. */
function popcount(x: number): number {
  x -= (x >>> 1) & 0x55555555;
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  return (((x + (x >>> 4)) & 0x0f0f0f0f) * 0x01010101) >>> 24;
}

/** Distance de Hamming entre deux empreintes hexadécimales de 16 chiffres. */
export function distance(a: string, b: string): number {
  return (
    popcount((parseInt(a.slice(0, 8), 16) ^ parseInt(b.slice(0, 8), 16)) >>> 0) +
    popcount((parseInt(a.slice(8), 16) ^ parseInt(b.slice(8), 16)) >>> 0)
  );
}

/**
 * **Les groupes de doublons** : pour chaque `fid` qui a au moins un voisin, l'identifiant
 * de son groupe — le `fid` du premier membre rencontré. Une entité seule n'y figure pas.
 */
export function duplicateGroups(
  entities: readonly Fingerprinted[],
  threshold: number,
): ReadonlyMap<string, string> {
  const fids: string[] = [];
  const hi: number[] = [];
  const lo: number[] = [];
  const visual: number[] = [];
  const bySha = new Map<string, number[]>();

  for (const e of entities) {
    if (!e.Fid) continue;
    const i = fids.length;
    fids.push(e.Fid);
    if (e.VisualHash && HEX16.test(e.VisualHash)) {
      hi[i] = parseInt(e.VisualHash.slice(0, 8), 16);
      lo[i] = parseInt(e.VisualHash.slice(8), 16);
      visual.push(i);
    }
    if (e.Sha256) {
      const same = bySha.get(e.Sha256);
      if (same) same.push(i);
      else bySha.set(e.Sha256, [i]);
    }
  }

  // **Union-find**, avec compression de chemin : chaque voisinage fusionne deux
  // groupes, et la racine d'un indice se retrouve en temps quasi constant.
  const parent = fids.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!;
      i = parent[i]!;
    }
    return i;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  for (const same of bySha.values()) for (let k = 1; k < same.length; k++) union(same[0]!, same[k]!);

  const limit = Math.max(0, Math.min(MAX_THRESHOLD, Math.floor(threshold)));
  for (let a = 0; a < visual.length; a++) {
    const i = visual[a]!;
    const h = hi[i]!;
    const l = lo[i]!;
    for (let b = a + 1; b < visual.length; b++) {
      const j = visual[b]!;
      if (popcount((h ^ hi[j]!) >>> 0) + popcount((l ^ lo[j]!) >>> 0) <= limit) union(i, j);
    }
  }

  const size = new Map<number, number>();
  for (let i = 0; i < fids.length; i++) {
    const r = find(i);
    size.set(r, (size.get(r) ?? 0) + 1);
  }
  const groups = new Map<string, string>();
  for (let i = 0; i < fids.length; i++) {
    const r = find(i);
    if (size.get(r)! > 1) groups.set(fids[i]!, fids[r]!);
  }
  return groups;
}

/**
 * **Le tri « doublons regroupés »** : les groupes d'abord, du plus récent au plus
 * ancien selon leur membre le plus récent, chaque groupe d'un seul tenant et trié
 * par date ; puis tout le reste, par date décroissante. Un tri qui rassemble mais
 * laisse le reste à sa place : l'écran ne devient pas vide de ce qui n'a pas de
 * doublon.
 */
export function sortByDuplicates<T extends Fingerprinted>(
  list: readonly T[],
  groups: ReadonlyMap<string, string>,
  dateOf: (e: T) => string,
): T[] {
  const newest = new Map<string, string>();
  for (const e of list) {
    const g = e.Fid ? groups.get(e.Fid) : undefined;
    if (g === undefined) continue;
    const d = dateOf(e);
    if ((newest.get(g) ?? '') < d) newest.set(g, d);
  }
  return [...list].sort((a, b) => {
    const ga = a.Fid ? groups.get(a.Fid) : undefined;
    const gb = b.Fid ? groups.get(b.Fid) : undefined;
    if (ga !== undefined && gb === undefined) return -1;
    if (ga === undefined && gb !== undefined) return 1;
    if (ga !== undefined && gb !== undefined && ga !== gb) {
      const byGroup = newest.get(gb)!.localeCompare(newest.get(ga)!);
      return byGroup !== 0 ? byGroup : ga.localeCompare(gb);
    }
    return dateOf(b).localeCompare(dateOf(a));
  });
}
